import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { API_URL, BRAND } from "../lib/brand";

export default function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !consent) {
      toast.error("Serve l'email e accettare l'informativa privacy");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/newsletter/subscribe`, { email, name });
      setDone(true);
      toast.success(data.message || "Ci sei! Presto ti scrivo 💗");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore. Riprova.";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <section className="px-6 md:px-10 py-16 md:py-24" data-testid="home-newsletter-section">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-[40px] bg-[#FF2768] p-8 md:p-16 shadow-2xl"
        >
          {/* Decorative shapes */}
          <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#FFE45C]/30 blur-3xl pointer-events-none" />
          <div className="absolute -left-20 -bottom-20 h-80 w-80 rounded-full bg-[#81C3C9]/40 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* LEFT — Bear logo in turquoise circle + tagline */}
            <div className="lg:col-span-4 text-center lg:text-left">
              <div className="inline-flex items-center justify-center h-40 w-40 md:h-52 md:w-52 rounded-full bg-[#81C3C9] mb-6 shadow-xl">
                <img src={BRAND.logo} alt="Teresa Vilardo Family Coach Montessori" className="h-32 w-32 md:h-44 md:w-44 rounded-full" />
              </div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] font-semibold">— La newsletter</p>
              <h2 className="font-editorial italic text-5xl md:text-6xl leading-[0.9] text-white mt-2">
                Una lettera<br/>alla volta <span className="text-[#FFE45C]">♡</span>
              </h2>
            </div>

            {/* RIGHT — Copy + form */}
            <div className="lg:col-span-8">
              <p className="text-white/95 text-lg md:text-xl leading-relaxed mb-6 font-editorial">
                Sguardi, pensieri e piccoli strumenti per accompagnare l'infanzia con cura.
                Ogni due settimane ti scrivo una lettera pensata: <em>non per parlarti addosso, ma per farti compagnia.</em>
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 text-sm">
                {[
                  { icon: "💬", label: "Una domanda per te" },
                  { icon: "🌱", label: "Uno spunto pratico" },
                  { icon: "📚", label: "Novità dalla Biblioteca T.A.T.A." },
                ].map((it) => (
                  <li key={it.label} className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-2 text-white font-medium">
                    <span>{it.icon}</span> {it.label}
                  </li>
                ))}
              </ul>

              {done ? (
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30" data-testid="newsletter-success">
                  <p className="inline-flex items-center gap-2 text-white font-editorial italic text-xl">
                    <CheckCircle2 size={22} className="text-[#FFE45C]" /> Ci sei! Presto ti scrivo 💗
                  </p>
                  <p className="text-white/80 text-sm mt-2">La prima lettera arriverà nella tua casella tra qualche giorno.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="newsletter-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="sr-only" htmlFor="nl-name">Nome</label>
                      <input
                        id="nl-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Il tuo nome (opzionale)"
                        className="w-full px-5 py-4 rounded-full bg-white/95 text-[#3D2140] placeholder-[#3D2140]/50 outline-none focus:bg-white shadow-sm"
                        data-testid="newsletter-name"
                      />
                    </div>
                    <div>
                      <label className="sr-only" htmlFor="nl-email">Email</label>
                      <input
                        id="nl-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="La tua email"
                        className="w-full px-5 py-4 rounded-full bg-white/95 text-[#3D2140] placeholder-[#3D2140]/50 outline-none focus:bg-white shadow-sm"
                        data-testid="newsletter-email"
                      />
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer text-white/90 text-sm">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="h-4 w-4 mt-1 accent-[#FFE45C]"
                      required
                      data-testid="newsletter-consent"
                    />
                    <span>
                      Ho letto la <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#FFE45C] underline hover:no-underline">Privacy Policy</a> e acconsento a ricevere <em>"Una lettera alla volta"</em>. Puoi disiscriverti quando vuoi.
                    </span>
                  </label>

                  <div className="flex flex-wrap items-center gap-4 pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#3D2140] text-white font-semibold uppercase tracking-wider text-sm hover:bg-black hover:gap-3 transition-all disabled:opacity-60 shadow-xl"
                      data-testid="newsletter-submit"
                    >
                      <Mail size={16} /> {loading ? "Un attimo…" : "Iscrivimi alla lettera"} <ArrowRight size={16} />
                    </button>
                    <p className="text-white/70 text-xs">Nessuno spam. Solo cura, ogni due settimane.</p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
